// src/components/auth/PasswordReset.js

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';

const PasswordReset = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPassword } = useAuth();
  const { companyInfo } = useTheme();
  const navigate = useNavigate();

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    try {
      const result = await resetPassword(email);
      setMessage(result.message);
      setIsSuccess(result.success);
      
      if (result.success) {
        setEmail(''); // Clear email on success
        // Redirect to login page with success parameter after a short delay
        setTimeout(() => {
          navigate('/login?reset=success');
        }, 2000);
      }
    } catch (err) {
      setMessage('An unexpected error occurred. Please try again.');
      setIsSuccess(false);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#111111]">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden max-w-md w-full"
      >
        <div className="p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex justify-center mb-4">
                <img 
                  src={companyInfo.favicon || companyInfo.logo || "/apex-logo-placeholder.png"} 
                  alt={companyInfo.name || "Apex Interio"} 
                  className="h-16 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzNiNDJjYyIvPjxwYXRoIGQ9Ik0zMCA3MEw1MCAzMEw3MCA3MFoiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=';
                  }} 
                />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Reset Password</h2>
              <p className="text-gray-500 mt-2">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </motion.div>
          </div>

          {message && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg mb-6 text-sm ${
                isSuccess 
                  ? 'bg-green-50 text-green-600 border border-green-200' 
                  : 'bg-red-50 text-red-600 border border-red-200'
              }`}
            >
              <div className="flex items-center">
                {isSuccess ? (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                {message}
              </div>
            </motion.div>
          )}

          <form onSubmit={handlePasswordReset}>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613] transition-colors"
                placeholder="Enter your email address"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-all ${
                isLoading || !email.trim()
                  ? 'bg-[#F9B3B7] cursor-not-allowed' 
                  : 'bg-[#E30613] hover:bg-[#C70511] active:bg-[#A00410]'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Email...
                </div>
              ) : (
                'Send Reset Email'
              )}
            </button>

            <div className="text-center mt-6">
              <p className="text-sm text-gray-600 mb-2">
                Remember your password?
              </p>
              <Link
                to="/login"
                className="text-[#E30613] hover:text-[#C70511] font-medium text-sm inline-flex items-center transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Login
              </Link>
            </div>
          </form>
        </div>
        
        <div className="bg-gray-50 p-4 text-center text-sm text-gray-600 border-t">
          <div className="mb-2">
            <strong>Need help?</strong>
          </div>
          <p>If you don't receive the email within a few minutes, check your spam folder or contact your administrator.</p>
        </div>
        
        <div className="bg-gray-100 p-4 text-center text-sm text-gray-500 border-t">
          © {new Date().getFullYear()} {companyInfo.name || "Apex Interio"}. All rights reserved.
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordReset;
